import type { BudgetForecastDataItem } from 'lumen/models';
import {
  COLUMN_PROPERTIES,
  type DataType,
} from 'lumen/utils/constants/budget-forecast-constants';
import { type Variance, VARIANCES } from 'lumen/utils/constants/variance';
import safeDivide from 'lumen/utils/numeric/safe-divide';

type BudgetForecastSubset = Pick<
  BudgetForecastDataItem,
  'rooms' | 'adr' | 'revenue'
> & {
  dataType?: DataType;
  roundedAdr?: number;
  roundedRevenue?: number;
  roundedRooms?: number;
};

type BudgetForecastOrPartial = BudgetForecastDataItem | BudgetForecastSubset;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function calculateVariance(
  base: BudgetForecastOrPartial | undefined | null,
  reference: BudgetForecastOrPartial | undefined,
  variance: Variance,
) {
  if (
    !base ||
    !reference ||
    base.dataType === reference.dataType ||
    variance === VARIANCES.NONE
  ) {
    return {
      [COLUMN_PROPERTIES.ROOMS]: null,
      [COLUMN_PROPERTIES.ADR]: null,
      [COLUMN_PROPERTIES.REVENUE]: null,
    };
  }

  const rooms =
    (reference.roundedRooms ?? reference.rooms) -
    (base.roundedRooms ?? base.rooms);
  const adr =
    (reference.roundedAdr ?? reference.adr) - (base.roundedAdr ?? base.adr);
  const revenue =
    (reference.roundedRevenue ?? reference.revenue) -
    (base.roundedRevenue ?? base.revenue);

  if (variance === VARIANCES.ABSOLUTE) {
    return {
      [COLUMN_PROPERTIES.ROOMS]: rooms,
      [COLUMN_PROPERTIES.ADR]: adr,
      [COLUMN_PROPERTIES.REVENUE]: revenue,
    };
  }

  if (variance === VARIANCES.PERCENTAGE) {
    return {
      [COLUMN_PROPERTIES.ROOMS]: !base.rooms
        ? null
        : (safeDivide(rooms, base.rooms) ?? 0) * 100,
      [COLUMN_PROPERTIES.ADR]: !base.adr
        ? null
        : (safeDivide(adr, base.adr) ?? 0) * 100,
      [COLUMN_PROPERTIES.REVENUE]: !base.revenue
        ? null
        : (safeDivide(revenue, base.revenue) ?? 0) * 100,
    };
  }

  return variance satisfies never;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function groupItemsByDate(items: BudgetForecastDataItem[]) {
  return items.reduce<Record<string, BudgetForecastDataItem[]>>((acc, item) => {
    const dateKey = item.date.format('YYYY-MM-DD');

    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }

    acc[dateKey].push(item);

    return acc;
  }, {});
}
